import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { EXPERIMENT_FLAGS } from '@scio3/shared-types';
import { SET_EXPERIMENT_VARIANT, type SetExperimentVariantResponse } from '@scio3/graphql-client';
import { isPosthogEnabled, posthog, trackEvent } from '@/lib/posthog';
import { useAuthStore } from '@/stores/auth-store';

/** Display variant the UI branches on: `a` = current welcome screen, `b` = test screen. */
export type WelcomeScreenVariant = 'a' | 'b';

/**
 * PostHog arm keys for the `welcome-screen` flag: `control` = current screen,
 * `b` = the WelcomeScreenTest. These are what PostHog assigns and what we persist
 * to `company.experiments['welcome-screen']` (only `b` maps to the test display arm).
 */
type WelcomeArm = 'control' | 'b';

const FLAG = EXPERIMENT_FLAGS.WELCOME_SCREEN; // 'welcome-screen'
// Person property that pins the flag to the resolved arm. The flag's release-condition
// Sets 1-2 (`ff_welcome_screen` equals control / b) force the matching arm, so a
// returning user always re-evaluates to the SAME variant across sessions/devices.
const PIN_PROPERTY = 'ff_welcome_screen';
const STORAGE_KEY = 'welcome-screen-variant';

/** Generous so a slow-but-working flag load is never pre-empted by a premature control answer. */
const FLAG_SAFETY_TIMEOUT_MS = 8000;
/** Retry "no matching condition set" — signup_date/platform props may still be propagating post-signup. */
const MAX_FLAG_RETRIES = 3;
const FLAG_RETRY_DELAY_MS = 1000;

// One-per-user guard so the exposure event + pin fire at most once per load.
let exposedFor: string | null = null;

function normalizeArm(value: unknown): WelcomeArm | null {
  return value === 'control' || value === 'b' ? value : null;
}

/** Only `b` renders the test arm; `control` / not-enrolled both fall through to `a`. */
function toDisplay(arm: WelcomeArm | null): WelcomeScreenVariant {
  return arm === 'b' ? 'b' : 'a';
}

/**
 * QA force override — short param `?ws=a` / `?ws=b` (non-prod only), persisted to
 * sessionStorage so it sticks across navigations. Returns the display variant, or
 * null → fall through to the PostHog resolution below.
 */
function readForcedWelcomeVariant(): WelcomeScreenVariant | null {
  if (typeof window === 'undefined') return null;
  if (import.meta.env.VITE_POSTHOG_ENVIRONMENT === 'production') return null;
  try {
    const param = new URL(window.location.href).searchParams.get('ws');
    if (param === 'a' || param === 'b') {
      sessionStorage.setItem(STORAGE_KEY, param);
      return param;
    }
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored === 'a' || stored === 'b' ? stored : null;
  } catch {
    return null;
  }
}

export interface WelcomeScreenVariantResult {
  variant: WelcomeScreenVariant;
  /**
   * True only until the first-ever resolve settles. Consumers that render per-arm UI
   * should hold while loading so the wrong arm never paints. Forced (`?ws=`) and
   * already-stored (sticky) arms return `false` — no flash on repeat visits.
   */
  isLoading: boolean;
}

/**
 * Variant for the `welcome-screen` A/B test — control (`a`) vs test (`b`).
 *
 * PostHog-driven + backend-sticky (modeled on the proven scio3b-split
 * `useOnboardingVariant`). Assignment + the new-users gate live in the PostHog
 * `welcome-screen` flag (release conditions: platform=scio3, role=0, signup_date
 * after the launch date); the backend just makes the pick stick.
 *
 * Resolution order:
 *  1. QA preview override (`?ws=a` / `?ws=b`, non-prod, sticky in sessionStorage).
 *  2. The value stored on the company (`experiments['welcome-screen']`) — sticky, so
 *     every consumer agrees and it never flips.
 *  3. First time only (nothing stored): wait for an IDENTIFIED PostHog answer, then
 *     persist write-once via `setExperimentVariant` + pin `ff_welcome_screen` +
 *     fire the exposure event. No SDK / timeout → control, NOT persisted (re-resolves
 *     next login).
 */
export function useWelcomeScreenVariant(): WelcomeScreenVariantResult {
  // Read the override once on mount — subsequent ?ws changes only take effect on a
  // full reload, matching how real assignment resolves.
  const [override] = useState<WelcomeScreenVariant | null>(readForcedWelcomeVariant);
  const company = useAuthStore((s) => s.company);
  const setCompany = useAuthStore((s) => s.setCompany);
  const userId = useAuthStore((s) => s.me?._id);

  // Source of truth: the arm stored on the company. Reactive — once one consumer
  // resolves + persists it below, every consumer reads it here.
  const storedArm = normalizeArm(company?.experiments?.[FLAG]);
  const accountId = company?._id;

  const [setExperimentVariant] = useMutation<SetExperimentVariantResponse>(SET_EXPERIMENT_VARIANT);

  // Session-locked fallback for the very first time, before the backend value exists.
  const [locked, setLocked] = useState<WelcomeArm | null>(() =>
    normalizeArm(useAuthStore.getState().company?.experiments?.[FLAG]),
  );

  useEffect(() => {
    if (override || locked || storedArm || !accountId) return; // already resolved

    // No SDK (no key / embedded iframe) → control; don't sit on the skeleton.
    if (!isPosthogEnabled) {
      setLocked('control');
      return;
    }

    let done = false;
    let retriesLeft = MAX_FLAG_RETRIES;
    let retryTimer: number | undefined;
    let forcedReload = false;

    // Write-once on the backend + optimistically patch the in-store company so every
    // other consumer reads the SAME value immediately (no first-session divergence).
    const persist = async (value: WelcomeArm) => {
      const current = useAuthStore.getState().company;
      if (current && !current.experiments?.[FLAG]) {
        setCompany({
          ...current,
          experiments: { ...(current.experiments ?? {}), [FLAG]: value },
        });
      }
      try {
        await setExperimentVariant({ variables: { key: FLAG, variant: value } });
      } catch {
        // Best-effort — the optimistic store value keeps this session consistent;
        // next login re-resolves and re-persists.
      }
    };

    // Accept ONLY an identified answer, so we never persist the anonymous bucket.
    const accept = () => {
      if (done) return;
      if (!posthog._isIdentified?.() || !userId || posthog.get_distinct_id?.() !== userId) return;

      // Being identified isn't enough: the pre-identify (anonymous) /flags values
      // linger in the SDK even after distinct_id flips, so force exactly one reload
      // now that we're identified and only trust the value its callback delivers.
      if (!forcedReload) {
        forcedReload = true;
        posthog.reloadFeatureFlags?.();
        return;
      }

      const raw = posthog.getFeatureFlag(FLAG);
      if (raw === 'control' || raw === 'b') {
        done = true;
        window.clearTimeout(retryTimer);
        setLocked(raw);
        void persist(raw);
        return;
      }

      // No cohort match yet (signup_date/platform may still be propagating) → retry.
      if (retriesLeft > 0) {
        retriesLeft -= 1;
        console.warn(`[experiment] ${FLAG}: no cohort match — retrying (${retriesLeft} left).`);
        retryTimer = window.setTimeout(() => posthog.reloadFeatureFlags?.(), FLAG_RETRY_DELAY_MS);
        return;
      }

      // Retries exhausted → not enrolled → show control, but do NOT persist (so a
      // genuine race victim re-resolves next login instead of being locked).
      done = true;
      window.clearTimeout(retryTimer);
      setLocked('control');
    };

    // onFeatureFlags fires synchronously if flags are already loaded, and again on
    // each reload — so both the already-loaded and the post-navigate-load cases are
    // handled without racing an unloaded getFeatureFlag().
    const unsub = posthog.onFeatureFlags?.(() => accept());

    const timeoutId = window.setTimeout(() => {
      if (done) return;
      done = true;
      // Timeout fallback is NOT persisted — a slow load must never lock control.
      console.error(
        `[experiment] ${FLAG}: no identified PostHog answer within ${FLAG_SAFETY_TIMEOUT_MS}ms — ` +
          `falling back to control. Check VITE_POSTHOG_KEY / VITE_POSTHOG_API_HOST.`,
      );
      setLocked('control');
    }, FLAG_SAFETY_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(retryTimer);
      unsub?.();
    };
  }, [override, storedArm, locked, userId, accountId, setCompany, setExperimentVariant]);

  // Pin PostHog's flag to the resolved arm via the `ff_welcome_screen` person property
  // (the flag's Sets 1-2 force the matching arm) + emit a one-per-user exposure event
  // so analysis can break down by `variant` independent of PostHog's bucketing. QA
  // `?ws=` overrides are excluded — a forced variant must never persist onto the person.
  const pinArm = storedArm ?? locked;
  useEffect(() => {
    if (!pinArm || !userId || !isPosthogEnabled) return;
    if (exposedFor === userId) return;
    exposedFor = userId;
    posthog.setPersonProperties({ [PIN_PROPERTY]: pinArm });
    posthog.setPersonPropertiesForFlags({ [PIN_PROPERTY]: pinArm }, true);
    trackEvent('welcome_screen_variant_exposure', { experiment: FLAG, variant: pinArm });
  }, [pinArm, userId]);

  if (override) return { variant: override, isLoading: false };
  if (storedArm) return { variant: toDisplay(storedArm), isLoading: false };
  if (locked) return { variant: toDisplay(locked), isLoading: false };
  // Still resolving the first identified answer → hold so the wrong arm never paints.
  return { variant: 'a', isLoading: true };
}
